import {
  type AgentRuntime,
  type Memory,
  type Provider,
  type State,
  logger,
} from "@elizaos/core";
import axios from "axios";
import { getApiConfig, validateCoingeckoConfig } from "../environment";

interface CategoryItem {
  category_id: string;
  name: string;
}

const CACHE_KEY = "coingecko:categories";
const CACHE_TTL = 5 * 60; // 5 minutes
const MAX_RETRIES = 3;

async function fetchCategories(runtime: AgentRuntime): Promise<CategoryItem[]> {
  const config = await validateCoingeckoConfig(runtime);
  const { baseUrl, apiKey, headerKey } = getApiConfig(config);

  const response = await axios.get<CategoryItem[]>(
    `${baseUrl}/coins/categories/list`,
    {
      headers: {
        accept: "application/json",
        [headerKey]: apiKey,
      },
      timeout: 5000, // 5 second timeout
    }
  );

  if (!response.data?.length) {
    throw new Error("Invalid categories data received");
  }

  return response.data;
}

async function fetchWithRetry(runtime: AgentRuntime): Promise<CategoryItem[]> {
  let lastError: Error | null = null;

  for (let i = 0; i < MAX_RETRIES; i++) {
    try {
      return await fetchCategories(runtime);
    } catch (error) {
      lastError = error;
      logger.error(`Categories fetch attempt ${i + 1} failed:`, error);
      await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
    }
  }

  throw (
    lastError || new Error("Failed to fetch categories after multiple attempts")
  );
}

// Simple in-memory cache
const cache: { data?: CategoryItem[]; timestamp?: number } = {};

async function getCategories(runtime: AgentRuntime): Promise<CategoryItem[]> {
  try {
    // Check in-memory cache
    if (
      cache.data &&
      cache.timestamp &&
      Date.now() - cache.timestamp < CACHE_TTL * 1000
    ) {
      return cache.data;
    }

    // Fetch fresh data
    const categories = await fetchWithRetry(runtime);

    // Update cache
    cache.data = categories;
    cache.timestamp = Date.now();

    return categories;
  } catch (error) {
    logger.error("Error fetching categories:", error);
    throw error;
  }
}

function formatCategoriesContext(categories: CategoryItem[]): string {
  const popularCategories = [
    "layer-1",
    "defi",
    "meme",
    "ai-meme-coins",
    "artificial-intelligence",
    "gaming",
    "metaverse",
  ];

  const popular = categories
    .filter((c) => popularCategories.includes(c.category_id))
    .map((c) => `${c.name} (${c.category_id})`);

  return `
Available cryptocurrency categories:

Popular categories:
${popular.map((c) => `- ${c}`).join("\n")}

Total available categories: ${categories.length}

You can use these category IDs when filtering cryptocurrency market data.
`.trim();
}

export const categoriesProvider: Provider = {
  name: "CATEGORIES_PROVIDER",
  // eslint-disable-next-line
  get: async (runtime: AgentRuntime, message: Memory, state: State) => {
    try {
      const categories = await getCategories(runtime);
      return {
        text: formatCategoriesContext(categories),
        data: {
          categories: categories,
        },
      };
    } catch (error) {
      logger.error("Categories provider error:", error);
      return {
        text: "Cryptocurrency categories are temporarily unavailable. Please try again later.",
      };
    }
  },
};

// Helper function for actions to get raw categories data
export async function getCategoriesData(
  runtime: AgentRuntime
): Promise<CategoryItem[]> {
  return getCategories(runtime);
}

// Helper function to clear cache (mainly for testing)
export function clearCategoriesCache(): void {
  cache.data = undefined;
  cache.timestamp = undefined;
}
